import {
  FEED_PAGE_MAX,
  type FeedPage,
  type FeedPriority,
  type FeedRow,
  type FeedStats,
  type FeedStatsRead,
  type FeedSuggestion,
  SUGGESTION_PAGE_MAX,
  SUGGESTION_SKIP_DAYS,
  type WatchDataset,
  feedRecordKey,
  feedSummaryDue,
} from '@parcel/types';
import { toast } from '@parcel/ui';
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { ApiError, apiFetch } from './api.js';

export type {
  FeedItem,
  FeedPage,
  FeedPriority,
  FeedRow,
  FeedStats,
  FeedStatsRead,
  FeedSuggestion,
} from '@parcel/types';

type FeedRecord = Pick<FeedRow, 'dataset' | 'record_id'>;
type FeedRows = InfiniteData<FeedPage, string | null>;

const STATS_POLL_MS = 60_000;

export const feedKeys = {
  all: (workspaceId: string | undefined) => ['feed', workspaceId] as const,
  rows: (workspaceId: string | undefined) => ['feed', workspaceId, 'rows'] as const,
  stats: (workspaceId: string | undefined) => ['feed', workspaceId, 'stats'] as const,
  suggested: (workspaceId: string | undefined) => ['feed', workspaceId, 'suggested'] as const,
};

export interface SuggestionPage {
  rows: FeedSuggestion[];
  next_offset: number | null;
}

/** One page of Suggested to follow, best first. */
export function fetchSuggestions(workspaceId: string, offset: number, dataset?: WatchDataset) {
  const query = new URLSearchParams({
    workspace_id: workspaceId,
    offset: String(offset),
    limit: String(SUGGESTION_PAGE_MAX),
  });
  if (dataset) query.set('dataset', dataset);
  return apiFetch<SuggestionPage>(`/feed/suggested?${query}`);
}

/**
 * Suggested to follow, a page at a time. `followed` is what this visit followed from the cards: those
 * stay on screen but have left the server's list, so the next page starts after the rows still live.
 */
export function useSuggestions(
  workspaceId: string | undefined,
  followed: ReadonlySet<string> = new Set(),
) {
  const followedRef = useRef(followed);
  followedRef.current = followed;
  return useInfiniteQuery({
    queryKey: feedKeys.suggested(workspaceId),
    queryFn: ({ pageParam }) => fetchSuggestions(workspaceId as string, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last: SuggestionPage, pages: SuggestionPage[]) => {
      if (last.next_offset == null) return undefined;
      const live = new Set(pages.flatMap((page) => page.rows).map(feedRecordKey));
      for (const key of followedRef.current) live.delete(key);
      return live.size;
    },
    // A 404 is an API without the route yet; asking again changes nothing.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 3,
    // A card followed this visit stays until the next load, so neither a refocus nor a reconnect pulls it away.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(workspaceId),
  });
}

const suggestionWritesKey = (workspaceId: string | undefined) =>
  [...feedKeys.suggested(workspaceId), 'write'] as const;

/** A skip or an Undo still on its way: a read now would not see it yet. */
export function useSuggestionWritesPending(workspaceId: string | undefined): boolean {
  return useIsMutating({ mutationKey: suggestionWritesKey(workspaceId) }) > 0;
}

/** Refetch the News feed after a change; Suggested only goes stale, so its cards stay until the next load. */
export function invalidateFeed(queryClient: QueryClient, workspaceId: string | undefined) {
  void queryClient.invalidateQueries({
    queryKey: feedKeys.all(workspaceId),
    predicate: (query) => query.queryKey[2] !== 'suggested',
  });
  void queryClient.invalidateQueries({
    queryKey: feedKeys.suggested(workspaceId),
    refetchType: 'none',
  });
}

/**
 * Skip drops the record at once and offers Undo; Undo, or a skip the server refused, puts it back where it stood.
 * Neither refetches: the list is only marked stale, so a card followed this visit stays until the next load.
 */
export function useSuggestionMutations(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const key = feedKeys.suggested(workspaceId);
  const dropped = useRef(new Map<string, { page: number; index: number; row: FeedSuggestion }>());
  // A refetch already in flight would land after the drop and bring the row back.
  const drop = async (item: FeedRecord) => {
    await queryClient.cancelQueries({ queryKey: key });
    const target = feedRecordKey(item);
    const data = queryClient.getQueryData<InfiniteData<SuggestionPage, number>>(key);
    data?.pages.forEach((page, p) => {
      const index = page.rows.findIndex((row) => feedRecordKey(row) === target);
      const row = page.rows[index];
      if (row) dropped.current.set(target, { page: p, index, row });
    });
    queryClient.setQueryData<InfiniteData<SuggestionPage, number>>(key, (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              rows: page.rows.filter((row) => feedRecordKey(row) !== target),
            })),
          }
        : current,
    );
  };
  const restore = (item: FeedRecord) => {
    const target = feedRecordKey(item);
    const spot = dropped.current.get(target);
    dropped.current.delete(target);
    if (!spot) return;
    queryClient.setQueryData<InfiniteData<SuggestionPage, number>>(key, (current) => {
      const page = current?.pages[Math.min(spot.page, current.pages.length - 1)];
      if (!current || !page || page.rows.some((row) => feedRecordKey(row) === target)) {
        return current;
      }
      const rows = [...page.rows];
      rows.splice(Math.min(spot.index, rows.length), 0, spot.row);
      return {
        ...current,
        pages: current.pages.map((candidate) =>
          candidate === page ? { ...page, rows } : candidate,
        ),
      };
    });
  };
  const post = (mode: 'skip' | 'unskip') => (item: FeedRecord) =>
    checkedItems(
      apiFetch<FeedWriteResults>(
        `/feed/suggested/${mode}?${new URLSearchParams({ workspace_id: workspaceId as string })}`,
        {
          method: 'POST',
          body: JSON.stringify({ dataset: item.dataset, record_id: item.record_id }),
        },
      ),
    );
  // A refused item carries the server's reason; an HTTP failure is worth a retry.
  const failed = (fallback: string) => (err: unknown) =>
    toast.error(err instanceof Error && !(err instanceof ApiError) ? err.message : fallback);
  const settle = () => queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
  const unskip = useMutation({
    mutationKey: suggestionWritesKey(workspaceId),
    mutationFn: post('unskip'),
    onMutate: restore,
    onError: (err, item) => {
      void drop(item);
      failed("Couldn't bring that suggestion back — please try again.")(err);
    },
    onSettled: settle,
  });
  return {
    skip: useMutation({
      mutationKey: suggestionWritesKey(workspaceId),
      mutationFn: post('skip'),
      onMutate: drop,
      onSuccess: (_out, item) =>
        toast.success(`Skipped for ${SUGGESTION_SKIP_DAYS} days.`, {
          action: { label: 'Undo', onClick: () => unskip.mutate(item) },
        }),
      onError: (err, item) => {
        restore(item);
        failed("Couldn't skip that suggestion — please try again.")(err);
      },
      onSettled: settle,
    }),
    unskip,
  };
}

/** One page of every followed record with news; the Feed reads it unfiltered. */
export function fetchFeedPage(workspaceId: string, cursor: string | null, limit = FEED_PAGE_MAX) {
  const query = new URLSearchParams({ workspace_id: workspaceId, limit: String(limit) });
  if (cursor) query.set('cursor', cursor);
  return apiFetch<FeedPage>(`/feed?${query}`);
}

export function fetchFeedStats(workspaceId: string) {
  return apiFetch<FeedStatsRead>(
    `/feed/stats?${new URLSearchParams({ workspace_id: workspaceId })}`,
  );
}

interface FeedWriteResults {
  results: { index: number; status: 'ok' | 'error'; error?: { code: string; message: string } }[];
  as_of?: string;
}

/** The batch routes answer 200 with per-item results; a failed item fails the write here. */
async function checkedItems(write: Promise<FeedWriteResults>): Promise<FeedWriteResults> {
  const response = await write;
  const failed = response.results.find((item) => item.status === 'error');
  if (failed) throw new Error(failed.error?.message ?? 'The News feed write failed.');
  return response;
}

/** Seen as of each row's changed_at, so news that lands after the row was shown stays unseen. */
export function markFeedSeen(
  workspaceId: string,
  rows: readonly Pick<FeedRow, 'dataset' | 'record_id' | 'changed_at'>[],
) {
  const inputs = rows.map((row) => ({
    dataset: row.dataset,
    record_id: row.record_id,
    as_of: row.changed_at,
  }));
  return checkedItems(
    apiFetch<FeedWriteResults>(
      `/feed/seen/batch?${new URLSearchParams({ workspace_id: workspaceId })}`,
      { method: 'POST', body: JSON.stringify({ inputs }) },
    ),
  );
}

/** Whether the API writes a summary for any of these first-page rows after answering. */
export function summariesPending(rows: readonly FeedRow[], now = Date.now()): boolean {
  return rows.some((row) => feedSummaryDue(row, now));
}

export function useFeedRows(workspaceId: string | undefined) {
  return useInfiniteQuery({
    queryKey: feedKeys.rows(workspaceId),
    queryFn: ({ pageParam }) => fetchFeedPage(workspaceId as string, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: Boolean(workspaceId),
  });
}

export function useFeedStats(workspaceId: string | undefined) {
  return useQuery({
    queryKey: feedKeys.stats(workspaceId),
    queryFn: () => fetchFeedStats(workspaceId as string),
    enabled: Boolean(workspaceId),
    refetchInterval: STATS_POLL_MS,
  });
}

export function useFeedUnseenCount(workspaceId: string | undefined): number {
  return useFeedStats(workspaceId).data?.unseen_rows ?? 0;
}

/**
 * Mark read flips the cached row and the badge at once; writes run one at a time, in order. A success refetches only
 * the badge (rows keep their flip, so a row read on this visit stays put); a failure refetches everything.
 */
export function useFeedSeenMutations(workspaceId: string | undefined) {
  const client = useQueryClient();
  const isTarget = (target: FeedRecord) => (row: FeedRow) =>
    row.dataset === target.dataset && row.record_id === target.record_id;

  /** Flips matching rows in every cached page, then sets the badge from its count and the rows flipped. */
  const flip = async (
    matches: (row: FeedRow) => boolean,
    unseen: boolean,
    badge: (count: number, flipped: number) => number,
  ) => {
    await client.cancelQueries({ queryKey: feedKeys.stats(workspaceId) });
    const flipped = setRows(matches, unseen);
    client.setQueryData<FeedStatsRead>(feedKeys.stats(workspaceId), (stats) =>
      stats && stats.unseen_rows !== null
        ? { ...stats, unseen_rows: Math.max(0, badge(stats.unseen_rows, flipped)) }
        : stats,
    );
  };
  /** Sets unseen on matching cached rows; answers how many changed. */
  const setRows = (matches: (row: FeedRow) => boolean, unseen: boolean) => {
    const flipped = new Set<string>();
    client.setQueriesData<FeedRows>(
      { queryKey: feedKeys.rows(workspaceId) },
      (data) =>
        data && {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows.map((row) => {
              if (!matches(row) || row.unseen === null || row.unseen === unseen) return row;
              flipped.add(feedRecordKey(row));
              return { ...row, unseen };
            }),
          })),
        },
    );
    return flipped.size;
  };
  const scope = { id: `feed-seen:${workspaceId}` };
  const mutationKey = ['feed-seen', workspaceId];
  // The badge refetches after the last queued write, so a server count never undoes a queued write's flip.
  // A refetch that landed while the write was in flight may have put the old state back; the flip goes on again.
  const onSuccess = (matches: (row: FeedRow) => boolean, unseen: boolean) => {
    setRows(matches, unseen);
    if (client.isMutating({ mutationKey }) <= 1) {
      void client.invalidateQueries({ queryKey: feedKeys.stats(workspaceId) });
    }
  };
  const onError = () => invalidateFeed(client, workspaceId);

  const markSeen = useMutation({
    mutationFn: (row: FeedRow) => markFeedSeen(workspaceId as string, [row]),
    onMutate: (row) => flip(isTarget(row), false, (count, flipped) => count - flipped),
    onSuccess: (_data, row) => onSuccess(isTarget(row), false),
    onError,
    scope,
    mutationKey,
  });
  return { markSeen };
}
